import React from 'react';

const ChatHistorySidebar = ({ 
  conversations, 
  currentConversationId, 
  onSelectConversation, 
  onNewChat,
  onDeleteConversation,
  onAnalyzeCode,
  onStopAnalyze,
  isAnalyzing = false,
  isLoading,
  availableModels = [],
  currentModel = null,
  currentThinkingLevel = 'low',
  sliderValue = 2,
  isCustomSelection = false,
  onModelChange,
  onThinkingLevelChange,
  onSliderChange,
  showResponseMode = true
}) => {
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [conversationToDelete, setConversationToDelete] = React.useState(null);
  const [showAdvancedControls, setShowAdvancedControls] = React.useState(false);

  const thinkingLevels = ['minimal', 'low', 'medium', 'high'];
  const isMinimalSupported = (modelName) => modelName === 'gemini-3-flash-preview';
  const normalizedSlider = Number.isFinite(sliderValue) ? sliderValue : 2;
  const speedScore = Math.max(1, 4 - normalizedSlider);
  const smartScore = Math.max(1, normalizedSlider);

  const handleSliderChange = (value) => {
    if (onSliderChange) {
      onSliderChange(value);
    }
  };
  
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const truncateTitle = (title, maxLength = 35) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength - 3) + '...';
  };

  const handleDeleteClick = (e, conversation) => {
    e.stopPropagation();
    setConversationToDelete(conversation);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (conversationToDelete) {
      onDeleteConversation(conversationToDelete.id);
      setShowDeleteModal(false);
      setConversationToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setConversationToDelete(null);
  };

  return (
    <div className="flex flex-col h-full" style={{backgroundColor: 'var(--tqa-surface-2)', borderLeft: '1px solid var(--tqa-border-dark)'}}>
      {/* Header */}
      <div className="p-4 space-y-2" style={{borderBottom: '1px solid var(--tqa-border-dark)'}}>
        <button
          onClick={onNewChat}
          className="w-full btn-primary py-1.5 px-3 text-xs font-medium flex items-center justify-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Chat
        </button>
        {onAnalyzeCode && (
          <button
            onClick={isAnalyzing ? onStopAnalyze : onAnalyzeCode}
            className={`w-full py-1.5 px-3 text-xs font-medium flex items-center justify-center gap-1.5 ${
              isAnalyzing ? 'bg-red-600 text-white rounded hover:bg-red-700' : 'btn-primary'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" />
            </svg>
            {isAnalyzing ? 'Stop' : 'Analyze Code'}
          </button>
        )}
      </div>

      {/* AI Model + Thinking Selector */}
      {showResponseMode && currentModel && availableModels.length > 0 && !currentModel.error && (
        <div className="p-2.5" style={{borderBottom: '1px solid var(--tqa-border-dark)'}}>
          <div className="mb-2">
            <span className="text-xs font-semibold brand-kicker">Response Mode</span>
          </div>

          <div className="space-y-2">
            <div>
              <input
                type="range"
                min="1"
                max="3"
                step="1"
                value={sliderValue}
                onChange={(e) => handleSliderChange(parseInt(e.target.value, 10))}
                className="w-full"
              />
              <div className="flex items-center justify-between text-[11px] font-medium mt-1" style={{color: 'rgba(255,255,255,0.4)'}}>
                <span>Faster</span>
                <span>Smarter</span>
              </div>
            </div>

            <div className="p-1.5" style={{border: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface-3)'}}>
              <div className="space-y-1.5">
                <div className="flex h-4 items-center gap-2" aria-label="Speed score">
                  <span className="w-9 text-[11px] font-medium leading-none" style={{color: 'rgba(255,255,255,0.4)'}}>Speed</span>
                  {[1, 2, 3].map((bar) => (
                    <div
                      key={`speed-${bar}`}
                      className={`h-1.5 flex-1 transition-all duration-300 ease-out`} style={{backgroundColor: bar <= speedScore ? 'var(--tqa-green)' : 'rgba(255,255,255,0.12)'}}
                    />
                  ))}
                </div>
                <div className="flex h-4 items-center gap-2" aria-label="Intelligence score">
                  <span className="w-9 text-[11px] font-medium leading-none" style={{color: 'rgba(255,255,255,0.4)'}}>Smart</span>
                  {[1, 2, 3].map((bar) => (
                    <div
                      key={`smart-${bar}`}
                      className={`h-1.5 flex-1 transition-all duration-300 ease-out`} style={{backgroundColor: bar <= smartScore ? 'var(--tqa-green)' : 'rgba(255,255,255,0.12)'}}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="overflow-hidden" style={{border: '1px solid var(--tqa-border-dark)'}}>
              <button
                type="button"
                onClick={() => setShowAdvancedControls((prev) => !prev)}
                className="w-full px-2.5 py-1.5 transition-colors flex items-center justify-between" style={{backgroundColor: 'var(--tqa-surface-2)'}}
              >
                <div className="text-left">
                  <p className="text-xs font-semibold" style={{color: 'rgba(255,255,255,0.75)'}}>Advanced selection</p>
                  <p className="text-[11px] truncate" style={{color: 'rgba(255,255,255,0.4)'}}>
                    {currentModel.displayName || currentModel.modelName} • {(currentThinkingLevel || 'low').toLowerCase()}
                  </p>
                </div>
                <svg
                  className={`w-4 h-4 transition-transform ${showAdvancedControls ? 'rotate-180' : ''}`} style={{color: 'rgba(255,255,255,0.4)'}}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showAdvancedControls && (
                <div className="p-2.5 space-y-2" style={{borderTop: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface-3)'}}>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color: 'rgba(255,255,255,0.5)'}}>Model</label>
                    <select
                      value={currentModel.modelName || ''}
                      onChange={(e) => onModelChange && onModelChange(e.target.value)}
                      className="w-full px-2 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.75)'}}
                    
                    >
                      {availableModels.map((model) => (
                        <option key={model.modelName} value={model.modelName}>
                          {model.displayName || model.modelName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color: 'rgba(255,255,255,0.5)'}}>Thinking level</label>
                    <select
                      value={(currentThinkingLevel || 'low').toLowerCase()}
                      onChange={(e) => onThinkingLevelChange && onThinkingLevelChange(e.target.value)}
                      className="w-full px-2 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.75)'}}
                    
                    >
                      {thinkingLevels.map((level) => (
                        <option
                          key={level}
                          value={level}
                          disabled={level === 'minimal' && !isMinimalSupported(currentModel.modelName)}
                        >
                          {level}
                        </option>
                      ))}
                    </select>
                    {!isMinimalSupported(currentModel.modelName) && (
                      <p className="mt-1 text-[11px]" style={{color: 'rgba(255,255,255,0.4)'}}>minimal is only available for gemini-3-flash-preview.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-sm" style={{color: 'rgba(255,255,255,0.4)'}}>Loading...</div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-center px-4">
            <div className="text-sm" style={{color: 'rgba(255,255,255,0.4)'}}>
              No conversations yet.<br />Start a new chat!
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={`group relative p-3 cursor-pointer transition-colors ${conv.id === currentConversationId ? '' : ''}`}
                style={conv.id === currentConversationId ? {backgroundColor: 'rgba(130,255,207,0.1)', border: '1px solid rgba(130,255,207,0.4)'} : {backgroundColor: 'var(--tqa-surface-3)', border: '1px solid var(--tqa-border-dark)'}}
                onClick={() => onSelectConversation(conv.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" style={{color: conv.id === currentConversationId ? 'var(--tqa-green)' : 'rgba(255,255,255,0.85)'}}>
                      {truncateTitle(conv.displayTitle || conv.title)}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs" style={{color: 'rgba(255,255,255,0.4)'}}>
                        {formatTimestamp(conv.updatedAt)}
                      </span>
                      {conv._count && conv._count.messages > 0 && (
                        <span className="text-xs" style={{color: 'rgba(255,255,255,0.3)'}}>
                          • {conv._count.messages} msg{conv._count.messages !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Delete Button */}
                  <button
                    onClick={(e) => handleDeleteClick(e, conv)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded text-red-600"
                    title="Delete conversation"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && conversationToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="shadow-xl p-6 w-96" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
            <h3 className="text-lg font-semibold mb-4" style={{color: 'rgba(255,255,255,0.9)'}}>Delete Conversation</h3>
            <p className="text-sm mb-6" style={{color: 'rgba(255,255,255,0.65)'}}>
              Are you sure you want to delete the conversation "{truncateTitle(conversationToDelete.displayTitle || conversationToDelete.title, 50)}"?
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="btn-secondary px-4 py-2 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white" style={{backgroundColor: 'var(--tqa-red)'}}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatHistorySidebar;
